import { Button, TableProps } from '@cloudscape-design/components';
import { Task } from 'src/types/helios/Task';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { createDateFilterConfig } from 'src/components/filter/FilterDatePicker';
import { StatusIndicator } from '@cloudscape-design/components';
import {
  getTaskStatusType,
  getTaskStatusColor,
  getTaskStatusLabel,
} from 'src/contstant/taskStatusColors';
import { formatDateTime } from 'src/utils/generalUtils';
import { getTaskDetailsUrl } from 'src/helper/taskHelper';
import { getTaskContent } from 'src/helper/taskHelper';
import { Link } from 'react-router-dom';

export enum TaskPropertyKey {
  TASK_ID = 'taskId',
  TASK_NAME = 'taskName',
  DIVISION = 'divisionName',
  QUESTION_ID = 'questionId',
  AUDIT_ID = 'auditId',
  TASK_TYPE = 'taskType',
  TASK_STATUS = 'taskStatus',
  DUE_DATE = 'dueDate',
  ASSIGNED_TO = 'assigneeLogin',
  LOCATION = 'locationId',
  BUSINESS_UNIT = 'businessUnit',
  CREATED_BY = 'createdBy',
  CREATED_DATE = 'createdAt',
}

export const initialFilters = [
  {
    propertyKey: TaskPropertyKey.TASK_STATUS,
    value: 'ASSIGNED',
    label: 'Assigned',
  },
  {
    propertyKey: TaskPropertyKey.TASK_STATUS,
    value: 'IN_PROGRESS',
    label: 'In Progress',
  },
  {
    propertyKey: TaskPropertyKey.TASK_STATUS,
    value: 'COMPLETED',
    label: 'Completed',
  },
  {
    propertyKey: TaskPropertyKey.TASK_STATUS,
    value: 'CANCELLED',
    label: 'Cancelled',
  },
  {
    propertyKey: TaskPropertyKey.TASK_STATUS,
    value: 'EXPIRED',
    label: 'Expired',
  },
];

export const getItemValue = (item: Task, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case TaskPropertyKey.TASK_ID:
      return {
        itemValue: item.taskId,
        type: FilterItemType.TEXT,
      };
    case TaskPropertyKey.TASK_STATUS:
      return {
        itemValue: item.taskStatus,
        type: FilterItemType.TEXT,
      };
    case TaskPropertyKey.DUE_DATE:
      return {
        itemValue: item.dueDate,
        type: FilterItemType.DATE,
      };
    case TaskPropertyKey.CREATED_DATE:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: Task) => [
  item.taskId || '',
  item.taskName || '',
  item.divisionName || '',
  item.questionId || '',
  item.questionTitle || '',
  item.auditId || '',
  item.auditName || '',
  item.taskStatus || '',
  item.assigneeLogin || '',
  item.locationId || '',
  item.businessUnit || '',
  item.createdBy || '',
];

export const getFilterTaskProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: any[] = [
    {
      key: TaskPropertyKey.TASK_ID,
      propertyLabel: 'Task ID',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Task ID values',
    },
    {
      key: TaskPropertyKey.TASK_STATUS,
      propertyLabel: 'Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Status values',
    },
    createDateFilterConfig({
      key: TaskPropertyKey.DUE_DATE,
      propertyLabel: 'Due Date',
      groupValuesLabel: 'Date',
    }),
    createDateFilterConfig({
      key: TaskPropertyKey.CREATED_DATE,
      propertyLabel: 'Created Date',
      groupValuesLabel: 'Date',
    }),
  ];

  if (isPlatformAdmin) {
    baseFilters.push({
      key: TaskPropertyKey.BUSINESS_UNIT,
      propertyLabel: 'Business Unit',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Business Unit values',
    });
  }

  return baseFilters;
};

export const getTasksTableDefinitions = (
  businessUnitList: any[] = [],
  selectedLanguage?: string,
): TableProps.ColumnDefinition<Task>[] => {
  const getBusinessUnitName = (businessUnitId: string | undefined) => {
    if (!businessUnitId) return '';
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return [
    {
      id: 'taskId',
      header: 'Task ID',
      cell: (item: Task) => item.taskId,
      isRowHeader: true,
    },
    {
      id: 'taskName',
      header: 'Task Name',
      cell: (item: Task) => (
        <div className="title-container">
          <Link target="_blank" rel="noopener noreferrer" to={getTaskDetailsUrl(item)}>
            <Button
              ariaLabel="View Task details (opens new tab)"
              iconAlign="right"
              iconName="external"
              variant="icon"
            />
          </Link>
          <Link className="title-hover" to={getTaskDetailsUrl(item)}>
            {getTaskContent(item, selectedLanguage).taskName || '-'}
          </Link>
        </div>
      ),
    },
    {
      id: 'businessUnit',
      header: 'Business Unit',
      cell: (item: Task) => getBusinessUnitName(item.businessUnit),
    },
    {
      id: 'divisionName',
      header: 'Division',
      cell: (item: Task) => item.divisionName,
    },
    {
      id: 'questionId',
      header: 'Question ID',
      cell: (item: Task) => item.questionId || '-',
    },
    {
      id: 'questionTitle',
      header: 'Question Title',
      cell: (item: Task) => getTaskContent(item, selectedLanguage).questionTitle || '-',
      maxWidth: 300,
      minWidth: 200,
    },
    {
      id: 'questionVersion',
      header: 'Question Version',
      cell: (item: Task) => item.questionVersion || '-',
    },
    {
      id: 'subQuestionId',
      header: 'SubQuestion ID',
      cell: (item: Task) => item.subQuestionId || '-',
    },
    {
      id: 'auditId',
      header: 'Audit ID',
      cell: (item: Task) => item.auditId,
    },
    {
      id: 'auditName',
      header: 'Audit Name',
      cell: (item: Task) => getTaskContent(item, selectedLanguage).auditName || '-',
      maxWidth: 300,
      minWidth: 200,
    },
    {
      id: 'templateName',
      header: 'Template Name',
      cell: (item: Task) => item.templateName || '-',
    },
    {
      id: 'taskType',
      header: 'Task Type',
      cell: (item: Task) => {
        const formatTaskType = (taskType: string) => {
          switch (taskType) {
            case 'FAILED_QUESTION':
              return 'Failed Question';
            case 'OBSERVATION':
              return 'Observation';
            case 'ADHOC':
              return 'Adhoc';
            default:
              return taskType || '-';
          }
        };
        return formatTaskType(item.taskType || '');
      },
    },
    {
      id: 'taskStatus',
      header: 'Status',
      cell: (item: Task) => (
        <StatusIndicator
          type={getTaskStatusType(item.taskStatus)}
          colorOverride={getTaskStatusColor(item.taskStatus)}
        >
          {getTaskStatusLabel(item.taskStatus)}
        </StatusIndicator>
      ),
    },
    {
      id: 'dueDate',
      header: 'Due Date',
      cell: (item: Task) => formatDateTime(item.dueDate, 'DD MMM YYYY'),
    },
    {
      id: 'assigneeLogin',
      header: 'Assigned To',
      cell: (item: Task) => item.assigneeLogin,
    },
    {
      id: 'locationId',
      header: 'Location',
      cell: (item: Task) => item.location,
    },
    {
      id: 'createdAt',
      header: 'Created Date',
      cell: (item: Task) => {
        return formatDateTime(item.createdAt);
      },
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: Task) => item.createdBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Last Updated At',
      cell: (item: Task) => formatDateTime(item.lastUpdatedAt, 'DD MMM YYYY'),
    },
    {
      id: 'lastUpdatedBy',
      header: 'Last Updated By',
      cell: (item: Task) => {
        if (!item.lastUpdatedBy) return '-';
        return item.lastUpdatedBy === 'TRANSLATION_SYSTEM'
          ? 'Translation System'
          : item.lastUpdatedBy;
      },
    },
  ];
};

export const contentDisplayPreference = {
  title: 'Column preferences',
  description: 'Customize which columns are shown',
  options: [
    { id: 'taskId', label: 'Task ID' },
    { id: 'taskName', label: 'Task Name' },
    { id: 'businessUnit', label: 'Business Unit' },
    { id: 'divisionName', label: 'Division' },
    { id: 'questionId', label: 'Question ID' },
    { id: 'questionTitle', label: 'Question Title' },
    { id: 'questionVersion', label: 'Question Version' },
    { id: 'subQuestionId', label: 'SubQuestion ID' },
    { id: 'auditId', label: 'Audit ID' },
    { id: 'auditName', label: 'Audit Name' },
    { id: 'templateName', label: 'Template Name' },
    { id: 'taskType', label: 'Task Type' },
    { id: 'taskStatus', label: 'Status' },
    { id: 'dueDate', label: 'Due Date' },
    { id: 'assigneeLogin', label: 'Assigned To' },
    { id: 'locationId', label: 'Location' },
    { id: 'createdAt', label: 'Created Date' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'lastUpdatedAt', label: 'Last Updated At' },
    { id: 'lastUpdatedBy', label: 'Last Updated By' },
  ],
};

export const contentDisplay = [
  { id: 'taskId', visible: true },
  { id: 'taskName', visible: true },
  { id: 'businessUnit', visible: true },
  { id: 'divisionName', visible: true },
  { id: 'questionId', visible: true },
  { id: 'questionTitle', visible: true },
  { id: 'questionVersion', visible: false },
  { id: 'subQuestionId', visible: true },
  { id: 'auditId', visible: true },
  { id: 'auditName', visible: true },
  { id: 'templateName', visible: true },
  { id: 'taskType', visible: true },
  { id: 'taskStatus', visible: true },
  { id: 'dueDate', visible: true },
  { id: 'assigneeLogin', visible: true },
  { id: 'locationId', visible: true },
  { id: 'createdAt', visible: true },
  { id: 'createdBy', visible: true },
  { id: 'lastUpdatedAt', visible: true },
  { id: 'lastUpdatedBy', visible: true },
];

export const defaultVisibleColumns = [
  'taskId',
  'taskName',
  'businessUnit',
  'divisionName',
  'questionId',
  'questionTitle',
  'auditId',
  'auditName',
  'templateName',
  'taskType',
  'taskStatus',
  'dueDate',
  'assigneeLogin',
];

export const pageSizePreference = {
  title: 'Page size preferences',
  options: [
    { value: 10, label: '10 items' },
    { value: 20, label: '20 items' },
    { value: 50, label: '50 items' },
  ],
};

export const getExportTasksConfig = (businessUnitList: any[] = []) => {
  const getBusinessUnitName = (businessUnitId: string | undefined) => {
    if (!businessUnitId) return '';
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return {
    fileName: 'tasks_list.csv',
    mappings: {
      'Task ID': 'taskId',
      'Task Name': (task: Task) =>
        task?.localizedContent?.content?.taskName || task.taskName || '',
      'Division': 'divisionName',
      'Question ID': 'questionId',
      'Question Title': 'questionTitle',
      'Question Version': 'questionVersion',
      'SubQuestion ID': 'subQuestionId',
      'Audit ID': 'auditId',
      'Audit Name': 'auditName',
      'Template Name': 'templateName',
      'Task Type': 'taskType',
      'Status': 'taskStatus',
      'Due Date': (task: Task) => formatDateTime(task.dueDate, 'DD MMM YYYY'),
      'Assigned To': 'assigneeLogin',
      'Location': 'location',
      'Business Unit': (task: Task) => getBusinessUnitName(task.businessUnit),
      'Created By': 'createdBy',
      'Created Date': (task: Task) => formatDateTime(task.createdAt),
      'Last Updated At': (task: Task) =>
        formatDateTime(task.lastUpdatedAt, 'DD MMM YYYY'),
      'Last Updated By': 'lastUpdatedBy',
    },
  };
};
